import { Loader2, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ConfigurationModalCategoriesNavigationProps } from '@/types/category';

import Alert from '@/assets/icons/alert.svg?react';
import { EmptyState } from '@/components/common/empty-state';
import { StatusMessage } from '@/components/common/status-message';
import { CategoryRow } from '@/components/configuration-modal/category-row';
import { NavigationMenu, NavigationMenuList } from '@/components/ui/navigation-menu';

export function ConfigurationModalCategoriesNavigation({
  isCategoriesLoading,
  isCategoriesError,
  refetchCategories,
  categories,
  categoryListItems,
  selectedCategoryId,
  onSelectCategoryId,
  onEditCategory,
  onDeleteCategory,
}: ConfigurationModalCategoriesNavigationProps) {
  const { t } = useTranslation();

  function renderCategoryListContent() {
    if (isCategoriesLoading) {
      return (
        <div className="text-muted-foreground flex w-fit flex-col items-center justify-center gap-2 px-16 py-8 text-sm">
          <Loader2 className="size-6 animate-spin" aria-hidden />
        </div>
      );
    }

    if (isCategoriesError) {
      return (
        <div className="text-muted-foreground flex w-fit flex-col items-center justify-center px-3 text-sm">
          <StatusMessage
            type="error"
            icon={<Alert className="text-destructive h-10 w-10" />}
            iconBgClassName="bg-destructive-subtle"
            title={t('common:configuration.controlMessages.categoriesError')}
            onAction={refetchCategories}
            actionLabel={t('common:configuration.controlMessages.categoriesRetry')}
          />
        </div>
      );
    }

    if (!categories?.length) {
      return (
        <div className="flex w-fit flex-col items-center justify-center px-2.5">
          <EmptyState
            icon={<SlidersHorizontal className="text-chat-cross" />}
            title={t('common:configuration.controlMessages.emptyCategories')}
            className="text-sm"
          />
        </div>
      );
    }

    return (
      <NavigationMenuList className="mx-4 flex-col overflow-auto">
        {categoryListItems.map((item) => (
          <CategoryRow
            key={item.id}
            item={item}
            selectedCategoryId={selectedCategoryId}
            onSelectCategoryId={onSelectCategoryId}
            onEdit={onEditCategory}
            onDelete={onDeleteCategory}
          />
        ))}
      </NavigationMenuList>
    );
  }

  return (
    <NavigationMenu
      orientation="vertical"
      aria-label={t('common:configuration.controlMessages.title')}
      className="border-separator w-1/5 flex-col overflow-auto border-r pb-4"
    >
      <div className="px-6 pt-6 pb-4">
        <div className="text-xl font-medium">{t('common:configuration.controlMessages.title')}</div>
        <div className="text-muted-foreground mt-1 text-sm">
          {t('common:configuration.controlMessages.activeCategories', {
            count: categories?.length ?? 0,
          })}
        </div>
      </div>
      {renderCategoryListContent()}
    </NavigationMenu>
  );
}
